import { Card, Flex, Input, Segmented, Space, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useState } from 'react';

import {
	CertDisposition,
	CertRegisterEntry,
	useGetCertRegisterQuery,
	useGetCertRegisterSummaryQuery,
} from '../../redux/api/material-certs';
import { DISPOSITIONS, DISPOSITION_ORDER } from './buckets';

const { Text } = Typography;
const PAGE_SIZE = 50;

/**
 * Every mill certificate that arrived by email, and what became of it.
 *
 * The queue next door only holds work in progress. This is the record: it
 * includes the certificates nothing could be done with, which are the ones
 * somebody eventually asks about.
 */
export default function RegisterTable() {
	const [disposition, setDisposition] = useState<CertDisposition | 'all'>('all');
	const [search, setSearch] = useState('');
	const [page, setPage] = useState(1);

	const { data: summary } = useGetCertRegisterSummaryQuery();
	const { data, isFetching } = useGetCertRegisterQuery({
		disposition: disposition === 'all' ? undefined : disposition,
		search: search || undefined,
		page,
		page_size: PAGE_SIZE,
	});

	const columns: ColumnsType<CertRegisterEntry> = [
		{
			title: 'Received',
			dataIndex: 'received_at',
			width: 150,
			render: (value: string | null) =>
				value ? dayjs(value).format('MMM D, YYYY h:mm A') : '--',
		},
		{ title: 'PO', dataIndex: 'po', width: 90 },
		{ title: 'Heat', dataIndex: 'heat', width: 140 },
		{ title: 'RT', dataIndex: 'rt', width: 120 },
		{
			title: 'Status',
			dataIndex: 'disposition',
			width: 150,
			render: (value: CertDisposition) => (
				<Tag color={DISPOSITIONS[value].color}>{DISPOSITIONS[value].label}</Tag>
			),
		},
		{
			title: 'What happened',
			dataIndex: 'disposition_detail',
			render: (value: string, row) => (
				<Space direction="vertical" size={0}>
					<Text>{value || '--'}</Text>
					{row.line_item && (
						<Text type="secondary" style={{ fontSize: 12 }}>
							{[row.line_item.type_name, row.line_item.shape_name]
								.filter(Boolean)
								.join(' ')}
						</Text>
					)}
				</Space>
			),
		},
		{
			title: 'Resolved by',
			dataIndex: 'resolved_by_email',
			width: 200,
			render: (value: string | null, row) =>
				value || (row.resolved_at ? 'System' : '--'),
		},
	];

	return (
		<Card
			size="small"
			style={{ height: '100%', overflow: 'auto' }}
			styles={{ body: { padding: 12 } }}
		>
			<Flex justify="space-between" align="center" wrap gap={8}>
				<Segmented
					value={disposition}
					onChange={(value) => {
						setDisposition(value as CertDisposition | 'all');
						setPage(1);
					}}
					options={[
						{ value: 'all', label: `All ${summary?.total ?? 0}` },
						...DISPOSITION_ORDER.map((key) => ({
							value: key,
							label: `${DISPOSITIONS[key].label} ${summary?.counts[key] ?? 0}`,
						})),
					]}
				/>
				<Input.Search
					allowClear
					placeholder="PO, heat, RT or subject"
					style={{ width: 280 }}
					onSearch={(value) => {
						setSearch(value.trim());
						setPage(1);
					}}
				/>
			</Flex>

			<Table
				rowKey="id"
				size="small"
				style={{ marginTop: 12 }}
				loading={isFetching}
				columns={columns}
				dataSource={data?.results ?? []}
				pagination={{
					current: page,
					pageSize: PAGE_SIZE,
					total: data?.count ?? 0,
					showSizeChanger: false,
					onChange: setPage,
				}}
			/>
		</Card>
	);
}
